/**
 * Relative wording for a requested start date, shown beneath the date itself
 * in the Open Demand → Resource Requests table.
 */

import { addMonths, differenceInCalendarDays, differenceInCalendarMonths, parseISO, startOfDay } from 'date-fns';

export interface StartDateProximity {
  label: string;
  passed: boolean;
}

export function getStartDateProximity(iso: string, now: Date = new Date()): StartDateProximity | null {
  if (!iso) return null;
  const start = startOfDay(parseISO(iso));
  if (Number.isNaN(start.getTime())) return null;
  const today = startOfDay(now);

  const days = differenceInCalendarDays(start, today);
  if (days < 0) return { label: '\u26A0 Start date passed', passed: true };
  if (days === 0) return { label: 'Today', passed: false };
  if (days === 1) return { label: 'Tomorrow', passed: false };
  if (days <= 30) return { label: `In ${days} days`, passed: false };

  const months = differenceInCalendarMonths(start, today);
  if (months < 1) return { label: `In ${days} days`, passed: false };

  const remainingDays = differenceInCalendarDays(start, addMonths(today, months));
  const monthPart = `${months} ${months === 1 ? 'month' : 'months'}`;
  if (remainingDays <= 0) return { label: `In ${monthPart}`, passed: false };

  const dayPart = `${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`;
  return { label: `In ${monthPart} ${dayPart}`, passed: false };
}
